//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { UnisonIconType } from '@bvengineering/unison-core';
import cx from 'classnames';
import React from 'react';
import styled from 'styled-components';
import { U } from 'ts-toolbelt';
import { Badge as CuBadge } from 'unison';

import Icon from '@components/atoms/icon/Icon';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';

export enum BadgeType {
  Primary = 'primary',
  Secondary = 'secondary',
  Success = 'success',
  Warning = 'warning',
  Error = 'error',
  NeedsAction = 'needs-action',
  Neutral = 'neutral',
  New = 'new',
  Focus = 'focus',
}

interface IOwnProps extends React.HTMLAttributes<HTMLDivElement> {
  type: BadgeType;
  size?: 'small' | 'default';
}

interface IIconBadgeProps extends IOwnProps {
  iconName: UnisonIconType;
}

interface ITextBadgeProps extends IOwnProps {
  iconName?: UnisonIconType;
  text: string;
  tooltipContent?: string;
}

type Props = U.Strict<IIconBadgeProps | ITextBadgeProps>;

/**
 * The Badge atom renders text, a single icon, or an icon followed by text in
 * a badge using a standard background, border, and
 * color combination. The badge will have a tooltip if
 * tooltipContent is provided.
 */
export const Badge = ({
  className,
  iconName,
  size = 'default',
  text,
  tooltipContent,
  type,
}: Props) => {
  const badgeClassnames = cx(className, `cu-badge-${type}`, {
    'cu-badge-icon': !!iconName && !text,
    'cu-badge-icon-text': !!iconName && !!text,
    'cu-badge-small': size === 'small',
  });

  return (
    <SmallTooltip
      content={tooltipContent}
      delay={{ showDelay: 500 }}
      isDisabled={!tooltipContent}
      target={
        <BadgeAtom className={badgeClassnames}>
          {iconName && <Icon name={iconName} />}
          {text}
        </BadgeAtom>
      }
    />
  );
};

const BadgeAtom = styled(CuBadge)(
  ({ theme }) => `
  /** Ensures Badge atoms are always one line of text */
  white-space: nowrap;
  cursor: default;

  /** Only applies when a badge renders both an icon and text */
  &.cu-badge-icon-text {
    display: inline-flex;
    align-items: center;
    gap: ${theme.spacings[1]};
  }
`,
);

export const BetaBadge = (props: Omit<IOwnProps, 'text' | 'type'>) => (
  <Badge text='beta' type={BadgeType.New} {...props} />
);

export const NewBadge = (props: Omit<IOwnProps, 'text' | 'type'>) => (
  <Badge text='new' type={BadgeType.New} {...props} />
);

export default Badge;
